import { Bar, BarChart, Cell, ReferenceLine, Text, XAxis, YAxis } from 'recharts';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { useIsMobile } from '@/hooks/use-mobile';
import type { OverzichtData } from './overzicht.api';

/**
 * Groei en afname elk hun eigen kleur, met dezelfde behandeling als de vlakken
 * in de grafiek hierboven: doorschijnende vulling, volle rand.
 *
 * Bewust `--primary` en `--destructive` en niet `--chart-1`/`--chart-2`: die
 * laatste zijn hierboven de identiteit van een onderwerp (blauw ís FMECA), en
 * hier gaat het over een richting. Semantisch de juiste tokens, en ze zien er
 * hetzelfde uit.
 */
const config = {
  verschil: { label: 'Verschil' },
  groei: { label: 'Meer dan vorige periode', color: 'var(--primary)' },
  afname: { label: 'Minder dan vorige periode', color: 'var(--destructive)' },
} satisfies ChartConfig;

/** Breed genoeg voor twee regels van een hoofdonderwerpnaam. */
const AS_BREEDTE = 190;

/** Hoogte per rij. Twee regels naam plus lucht eromheen. */
const RIJ_HOOGTE = 46;

/**
 * De naam op de as, afgekapt op twee regels.
 *
 * Waarom niet de standaard-tick: die gebruikt recharts' `Text`, die een lange
 * naam wél over meerdere regels breekt maar zonder bovengrens. "Vakinhoudelijke
 * verdieping Maintenance Engineering (incl. IAM) (nieuw)" wordt zo vier regels
 * hoog en loopt dwars door de rij eronder. `maxLines` kapt af met een
 * beletselteken; de volledige naam staat nog in de tooltip, want die valt terug
 * op het ruwe label zolang er geen `ChartConfig`-regel voor is.
 */
function NaamTick({
  payload,
  x = 0,
  y = 0,
}: {
  payload?: { value?: string | number };
  x?: number;
  y?: number;
}) {
  return (
    <Text
      className="fill-muted-foreground text-xs"
      maxLines={2}
      textAnchor="end"
      verticalAnchor="middle"
      width={AS_BREEDTE - 12}
      x={x}
      y={y}
    >
      {String(payload?.value ?? '')}
    </Text>
  );
}

/**
 * Wat erbij komt en wat afneemt: per onderwerp het verschil met de vorige
 * periode, naar rechts groei en naar links afname.
 *
 * Dit is het tegendeel van de opgetelde grafiek ernaast — die stijgt altijd en
 * kan dus niet laten zien dat iets terugloopt of net is opgekomen.
 */
export function VerschuivingenKaart({ data }: { data: OverzichtData }) {
  const { verschuivingen, vorige } = data;
  const telefoon = useIsMobile();

  // Symmetrische as, zodat een staaf van +5 net zo lang is als een van -5 en
  // de twee richtingen eerlijk tegen elkaar afgelezen kunnen worden.
  const grootste = Math.max(1, ...verschuivingen.map((r) => Math.abs(r.verschil)));

  const rijen = verschuivingen.map((r) => ({
    ...r,
    // De naam op de as vertelt meteen of iets nieuw is of juist weg.
    naam: r.nieuw ? `${r.label} (nieuw)` : r.weg ? `${r.label} (weg)` : r.label,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Wat erbij komt en wat afneemt</CardTitle>
        <CardDescription>
          Verschil met {vorige.label} · {data.periode.label}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {rijen.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Niets veranderd</EmptyTitle>
              <EmptyDescription>
                Geen onderwerp kwam in deze periode vaker of minder vaak voor dan in {vorige.label}.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : telefoon ? (
          <Verschuivingenlijst grootste={grootste} rijen={rijen} />
        ) : (
          <ChartContainer
            className="w-full"
            config={config}
            style={{ height: `${Math.max(180, rijen.length * RIJ_HOOGTE)}px` }}
          >
            <BarChart
              accessibilityLayer
              data={rijen}
              layout="vertical"
              margin={{ left: 8, right: 8 }}
            >
              <XAxis domain={[-grootste, grootste]} hide type="number" />
              <YAxis
                axisLine={false}
                dataKey="naam"
                tick={<NaamTick />}
                tickLine={false}
                tickMargin={8}
                type="category"
                width={AS_BREEDTE}
              />
              <ReferenceLine stroke="var(--border)" x={0} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(waarde) => {
                      const n = Number(waarde);
                      return `${n > 0 ? '+' : ''}${n} ten opzichte van ${vorige.label}`;
                    }}
                    hideIndicator
                  />
                }
                cursor={false}
              />
              <Bar dataKey="verschil" fillOpacity={0.4} radius={4} strokeWidth={1.5}>
                {rijen.map((r) => {
                  const kleur = r.verschil > 0 ? 'var(--color-groei)' : 'var(--color-afname)';
                  return <Cell fill={kleur} key={r.id} stroke={kleur} />;
                })}
              </Bar>
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Dezelfde cijfers op een telefoon, als lijst in plaats van als grafiek.
 *
 * De grafiek hierboven zet de namen in een kolom van 190 pixels naast de
 * staven. Op een telefoon is dat twee derde van het scherm: de namen staan dan
 * rechts uitgelijnd tegen een lege strook aan en de staven zijn te kort om nog
 * iets af te lezen. Naam boven de staaf in plaats van ernaast lost allebei op,
 * want dan is de volle breedte voor de staaf.
 *
 * Het getal staat erbij, en dat is winst die de grafiek niet heeft: daar zit
 * het in de tooltip, en op een telefoon is er niets om aan te wijzen.
 *
 * Met de hand en niet met recharts, omdat een balk hier niets anders is dan een
 * breedte in procenten. De nullijn in het midden houdt de twee richtingen
 * tegenover elkaar, net als de symmetrische as van de grafiek.
 */
function Verschuivingenlijst({
  grootste,
  rijen,
}: {
  grootste: number;
  rijen: ReadonlyArray<{ id: string; naam: string; verschil: number }>;
}) {
  return (
    <div className="flex flex-col gap-4">
      {rijen.map((r) => {
        const groei = r.verschil > 0;
        const deel = `${(Math.abs(r.verschil) / grootste) * 100}%`;
        const kleur = groei
          ? 'border-primary bg-primary/40'
          : 'border-destructive bg-destructive/40';

        return (
          <div className="flex flex-col gap-1.5" key={r.id}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-xs text-muted-foreground">{r.naam}</span>
              <span className="shrink-0 text-xs font-medium tabular-nums">
                {groei ? '+' : ''}
                {r.verschil}
              </span>
            </div>

            <div className="flex h-2 items-stretch">
              {/* Twee helften met de nullijn ertussen. Afname groeit vanaf het
                  midden naar links, dus die helft lijnt zijn balk rechts uit. */}
              <div className="flex flex-1 justify-end">
                {!groei && (
                  <div className={`rounded-l-sm border ${kleur}`} style={{ width: deel }} />
                )}
              </div>
              <div className="w-px shrink-0 bg-border" />
              <div className="flex-1">
                {groei && (
                  <div className={`rounded-r-sm border ${kleur}`} style={{ width: deel }} />
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
